/**
 * Locker/Nyckelhantering typer
 * Speglar locker/src/providers/interfaces/types.ts och enums.ts
 */

export enum CabinetStatus {
  Online = 'online',
  Offline = 'offline',
  Error = 'error',
  Maintenance = 'maintenance',
}

export enum KeyStatus {
  InCabinet = 'in_cabinet',
  Taken = 'taken',
  Lost = 'lost',
  Maintenance = 'maintenance',
}

export interface Cabinet {
  id: string;
  name: string;
  location?: string;
  capacity: number;
  occupiedSlots: number;
  listCount?: number;
  status: CabinetStatus;
}

export interface Key {
  id: string;
  name: string;
  cabinetId: string;
  slot?: number;
  list?: number;
  door?: number;
  status: KeyStatus;
  isAvailable: boolean;
  description?: string | null;
  keyNumber?: string | null;
  externalId?: string | null;
  currentReservation?: {
    userId?: string;
    groupId?: string;
    validUntil: string;
  };
}

export interface CabinetGroupAssignment {
  id: string;
  cabinet_id: string;
  cabinet_name: string | null;
  pickup_location_id: string | null;
  pickup_location_name?: string;
  organization_id: string;
  created_at: string;
  created_by: string;
}

export interface VehicleKeyAssignment {
  id: string;
  vehicle_id: string;
  vehicle_registration?: string;
  key_id: string;
  keywin_key_id?: string | null;
  key_name: string | null;
  keyRenamed?: boolean;
  keyNameRestored?: boolean;
  cabinet_id: string | null;
  organization_id: string;
  assigned_at: string;
  assigned_by: string;
  unassigned_at: string | null;
  unassigned_by: string | null;
  note: string | null;
}

export interface CabinetCustomerAssignment {
  id: number;
  cabinet_id: string;
  cabinet_name: string | null;
  organization_id: number;
  organization_name?: string;
  created_at: string;
  local_admin_pin?: string | null;
  local_admin_keywin_user_id?: string | null;
}

export interface CabinetSettings {
  id: number;
  organization_id: number;
  has_cabinet: number;
  keywin_location: string | null;
  super_admin_keywin_user_id: string | null;
  super_admin_pin: string | null;
}

export interface CabinetWithGroup extends Cabinet {
  pickupLocationId?: string;
  pickupLocationName?: string;
  customerOrganizationId?: number;
  customerOrganizationName?: string;
  localAdminPin?: string | null;
}

export interface KeyWithVehicle extends Key {
  vehicleId?: string;
  vehicleRegistration?: string;
  assignmentNote?: string | null;
}

/**
 * Loggpost från nyckelskåp (normaliserad från KeyWin IOT API)
 */
export interface LogEntry {
  id: number;
  timestamp: string;
  eventType: string;
  eventCode?: number;
  cabinetId?: number | string;
  cabinetName?: string;
  userId?: number;
  userName?: string;
  assetId?: number;
  assetName?: string;
  slot?: number;
  description?: string;
  isAlarm: boolean;
  isAcknowledged?: boolean;
  acknowledgedBy?: string;
  acknowledgedAt?: string;
}

/**
 * Sökparametrar för loggar
 */
export interface CabinetSyncQueue {
  name: string;
  kind: 'cabinet' | 'keywin' | 'other';
  messages: number;
  messagesReady: number;
  consumers: number;
  consumerOnline: boolean;
  publishRate: number;
  deliverRate: number;
  idleSince?: string | null;
  memory?: number;
}

export interface CabinetSyncEvent {
  id: number;
  ts: string;
  source?: 'rabbit' | 'locker-api' | 'cabinet-http';
  kind: string;
  method?: string | null;
  path?: string | null;
  status?: number | null;
  remoteIp?: string | null;
  exchange: string;
  routingKey: string;
  queue: string | null;
  cabinetId: string | null;
  bytes: number;
  contentType: string;
  summary: string;
  payload: unknown;
}

export interface CabinetSyncOverview {
  startedAt: string;
  spyConnected: boolean;
  traceEnabled: boolean;
  lastError: string | null;
  lastMgmtAt: string | null;
  rates: {
    eventsLastMinute: number;
    eventsLastHour: number;
    bytesLastHour: number;
    storedEvents: number;
  };
  queues: CabinetSyncQueue[];
  recentEvents: CabinetSyncEvent[];
}

export interface OpenVpnClient {
  cn: string;
  subject?: string;
  issuer?: string;
  serial?: string;
  thumbprint?: string;
  validFrom?: string;
  validTo?: string;
  locationName: string | null;
  organizationName: string | null;
  sourceRef: string;
  monitorId: number | null;
}

export interface IssuedOpenVpnProfile {
  cn: string;
  proto: 'tcp' | 'udp';
  filename: string;
  ovpn: string;
  subject: string;
  issuer: string;
  serial: string;
  thumbprint: string;
  validFrom: string;
  validTo: string;
}

export interface LogSearchParams {
  from?: string;
  to?: string;
  user?: string;
  cabinet?: string;
  event?: string;
  batchIndex?: number;
  batchSize?: number;
}
